export type StrategyRdJobKind =
  | "scan"
  | "apply"
  | "confirm"
  | "robustness"
  | "backtest"
  | "sweep"
  | "account"
  | string;

export type StrategyRdJobLifecycleStatus =
  | "queued"
  | "running"
  | "ready"
  | "failed"
  | "backfilling";

/** `GET /jobs/{id}` `progress` while a job runs. */
export type StrategyRdJobStage = {
  stage: "scanning" | "applying" | "searching" | "saving" | string;
  done?: number | null;
  total?: number | null;
};

export type StrategyRdJobProgress = {
  status: StrategyRdJobLifecycleStatus | string;
  kind?: StrategyRdJobKind;
  message: string;
  stage?: StrategyRdJobStage | null;
};

/** "Searching 3 / 9…" — the job's own stage, when it reports one. */
export function strategyRdJobStageLabel(stage: StrategyRdJobStage | null | undefined): string | null {
  const s = (stage?.stage || "").trim().toLowerCase();
  if (!s) return null;
  const count =
    stage?.done != null && stage?.total != null && stage.total > 0
      ? ` ${stage.done} / ${stage.total}`
      : "";
  if (s === "scanning") return "Scanning setups…";
  if (s === "applying") return "Running the backtest…";
  if (s === "searching") return `Trying setups${count}…`;
  if (s === "loading_data") return "Loading market history: the run continues by itself…";
  if (s === "saving") return "Saving…";
  return `${s.charAt(0).toUpperCase()}${s.slice(1)}${count}…`;
}

/** Prefer BE `user_message` while backfilling; then the job's stage; else lifecycle label. */
export function strategyRdJobProgressMessage(
  status: string,
  opts?: {
    kind?: StrategyRdJobKind | null;
    userMessage?: string | null;
    stage?: StrategyRdJobStage | null;
  }
): string {
  const s = (status || "").trim().toLowerCase();
  const fromBe = (opts?.userMessage || "").trim();
  if (s === "backfilling" && fromBe) return fromBe;
  if (s === "queued" || s === "running") {
    const staged = strategyRdJobStageLabel(opts?.stage);
    if (staged) return staged;
  }
  return strategyRdJobStatusLabel(status, opts?.kind);
}

/** Strip `/api` prefix so paths work with `authenticatedFetch` (`BACKEND_API_URL` already includes `/api`). */
export function strategyRdApiPathFromPollUrl(pollUrl: string): string {
  const p = (pollUrl || "").trim();
  if (!p) return "";
  if (p.startsWith("/api/")) return p.slice(4);
  return p.startsWith("/") ? p : `/${p}`;
}

export function isStrategyRdOhlcStripPollUrl(pollUrl: string | null | undefined): boolean {
  const p = (pollUrl || "").toLowerCase();
  return p.includes("ohlc-strips") || p.includes("execution-candles");
}

/** Backoff for Research job polls — not chat typing indicators. */
export function strategyRdJobPollIntervalMs(elapsedMs: number): number {
  if (elapsedMs < 10_000) return 1750;
  if (elapsedMs < 30_000) return 3000;
  return 4500;
}

export function strategyRdJobStatusLabel(
  status: string,
  kind?: StrategyRdJobKind | null
): string {
  const s = (status || "").trim().toLowerCase();
  const k = (kind || "").trim().toLowerCase();
  if (s === "queued") return "Queued…";
  if (s === "backfilling") return "Loading market data for this symbol…";
  if (s === "running") {
    if (k === "apply") return "Applying rules…";
    if (k === "confirm") return "Saving strategy…";
    if (k === "robustness") return "Testing against feed differences…";
    if (k === "backtest") return "Running the backtest…";
    if (k === "sweep") return "Searching settings…";
    if (k === "account") return "Running the account from every start date…";
    return "Scanning…";
  }
  if (s === "ready") return "Done";
  if (s === "failed") return "Failed";
  return "Working…";
}

export function isStrategyRdJobTerminalStatus(
  status: string,
  stopOn: ReadonlyArray<StrategyRdJobLifecycleStatus>
): boolean {
  const s = (status || "").trim().toLowerCase() as StrategyRdJobLifecycleStatus;
  return (stopOn as readonly string[]).includes(s);
}
